import React, { useState } from 'react'
import {
  ProgramFeedbackPayload,
  useFetchProgramBookings,
  useProgramBookingDetails,
  useSubmitProgramFeedback,
} from './api'
import { FiEye, FiDownload } from 'react-icons/fi'
import { BsThreeDotsVertical } from 'react-icons/bs'
import Icons from '../../../components/common/icons'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import { useSnackbarManager } from '../../../components/common/snackbar'

const DownloadIcon = FiDownload as React.ElementType
const EyeIcon = FiEye as React.ElementType
const MoreIcon = BsThreeDotsVertical as React.ElementType

const capitalizeFirstLetter = (str: string) => {
  if (!str) return str
  return str.charAt(0).toUpperCase() + str.slice(1)
}

const formatDateDDMMYYYY = (dateString: string) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  if (isNaN(date.getTime())) return ''
  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const year = date.getFullYear()
  return `${day}-${month}-${year}`
}

const getCurrentDateDDMMYYYY = () => {
  const now = new Date()
  const day = String(now.getDate()).padStart(2, '0')
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const year = now.getFullYear()
  return `${day}-${month}-${year}`
}

interface ProgramItem {
  id: number
  program_id: number | null
  event_id: number | null
  fees: string
  status: string
  booking_status: string
  created_at: string
  program?: {
    id: number
    name: string
  }
  event?: {
    id: number
    name: string
  }
  feedback_available?: boolean
  feedback_submitted?: boolean
}

const initialFeedback: ProgramFeedbackPayload = {
  overall_rating: 5,
  content_rating: 5,
  trainer_rating: 5,
  organization_rating: 5,
  comments: '',
  suggestions: '',
}

const ProgramFeedbackForm: React.FC<{
  booking: ProgramItem
  onClose: () => void
  onSubmitted: () => void
}> = ({ booking, onClose, onSubmitted }) => {
  const [form, setForm] = useState<ProgramFeedbackPayload>(initialFeedback)
  const { mutate, isLoading } = useSubmitProgramFeedback()
  const { enqueueSnackbar } = useSnackbarManager()
  const itemName = booking.program?.name || booking.event?.name || 'Program'

  const ratingFields: Array<{
    key: keyof ProgramFeedbackPayload
    label: string
  }> = [
    { key: 'overall_rating', label: 'Overall experience' },
    { key: 'content_rating', label: 'Content quality' },
    { key: 'trainer_rating', label: 'Trainer/facilitator' },
    { key: 'organization_rating', label: 'Organization and arrangements' },
  ]

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    mutate(
      { bookingId: booking.id, data: form },
      {
        onSuccess: (response: any) => {
          enqueueSnackbar(response?.message || 'Feedback submitted', {
            variant: 'success',
          })
          onSubmitted()
        },
        onError: (error: any) => {
          enqueueSnackbar(
            error?.response?.data?.message || 'Unable to submit feedback',
            { variant: 'error' }
          )
        },
      }
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <form
        onSubmit={submit}
        className="w-full max-w-xl rounded-2xl bg-white p-6 shadow-[0_25px_60px_rgba(0,0,0,0.25)] border border-gray-100 max-h-[90vh] overflow-y-auto"
      >
        <div className="mb-5 flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-md shadow-emerald-200">
              <span className="text-white text-lg">★</span>
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                Program Feedback
              </h2>
              <p className="mt-0.5 text-sm text-gray-500">{itemName}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700 flex items-center justify-center text-lg leading-none transition-colors duration-200"
            aria-label="Close feedback form"
          >
            ×
          </button>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          {ratingFields.map(({ key, label }) => (
            <label key={key} className="text-sm font-semibold text-gray-700">
              {label}
              <select
                value={String(form[key])}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    [key]: Number(event.target.value),
                  }))
                }
                className="mt-1.5 block w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:border-emerald-400 transition-all duration-200"
              >
                {[5, 4, 3, 2, 1].map((rating) => (
                  <option key={rating} value={rating}>
                    {rating} -{' '}
                    {rating === 5
                      ? 'Excellent'
                      : rating === 1
                        ? 'Poor'
                        : 'Good'}
                  </option>
                ))}
              </select>
            </label>
          ))}
        </div>

        <label className="mt-4 block text-sm font-semibold text-gray-700">
          Comments
          <textarea
            value={form.comments}
            onChange={(event) =>
              setForm((current) => ({
                ...current,
                comments: event.target.value,
              }))
            }
            rows={3}
            maxLength={5000}
            className="mt-1.5 block w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:border-emerald-400 transition-all duration-200"
            placeholder="Tell us about your experience"
          />
        </label>

        <label className="mt-4 block text-sm font-semibold text-gray-700">
          Suggestions
          <textarea
            value={form.suggestions}
            onChange={(event) =>
              setForm((current) => ({
                ...current,
                suggestions: event.target.value,
              }))
            }
            rows={3}
            maxLength={5000}
            className="mt-1.5 block w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:border-emerald-400 transition-all duration-200"
            placeholder="What could we improve?"
          />
        </label>

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 rounded-xl text-sm font-bold text-gray-600 bg-gray-100 border border-gray-200 hover:bg-gray-200 transition-colors duration-200"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="px-5 py-2.5 rounded-xl text-sm font-bold text-white bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 shadow-md hover:shadow-lg hover:scale-[1.03] active:scale-[0.97] disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-300"
          >
            {isLoading ? 'Submitting…' : 'Submit Feedback'}
          </button>
        </div>
      </form>
    </div>
  )
}

// const tableFields: { label: string; key: keyof ProgramItem | 'name' }[] = [
//   { label: 'ID', key: 'id' },
//   { label: 'Name', key: 'name' },
//   { label: 'Status', key: 'booking_status' },
//   { label: 'Applied Date', key: 'created_at' },
//   { label: 'Fees', key: 'fees' },
// ]

const ProgramDetail: React.FC<{ id: string; onBack: () => void }> = ({
  id,
  onBack,
}) => {
  const { data, isLoading, isError } = useProgramBookingDetails(id)
  const booking = data?.data

  if (isLoading) return <p className="p-4">Loading...</p>
  if (isError || !booking)
    return <p className="p-4 text-red-500">Failed to load details.</p>

  // Determine if it's a program or event booking
  const isEvent = booking.event_id !== null
  const item = isEvent ? booking.event : booking.program

  // Build item fields dynamically based on available data
  const itemFields = item
    ? [
        {
          label: isEvent ? 'Event Name' : 'Program Name',
          value: capitalizeFirstLetter(item.name),
        },
        ...(item.location ? [{ label: 'Location', value: item.location }] : []),
        ...(item.mode ? [{ label: 'Mode', value: item.mode }] : []),
        ...(item.status ? [{ label: 'Status', value: item.status }] : []),
        ...(item.participants
          ? [
              {
                label: 'Participants',
                value: `${item.participants} participants`,
              },
            ]
          : []),
        ...(item.seats
          ? [{ label: 'Seats', value: `${item.seats} seats` }]
          : []),
        ...(item.description
          ? [
              {
                label: 'Description',
                value: item.description.replace(/<[^>]*>/g, ''),
              },
            ]
          : []),
        ...(booking.fees
          ? [
              {
                label: 'Fees',
                value:
                  booking.fees === '0.00' || booking.fees === '0'
                    ? '0'
                    : `₹${parseFloat(booking.fees).toLocaleString('en-IN')}`,
              },
            ]
          : []),
        ...(isEvent
          ? [
              ...(item.event_start_date
                ? [
                    {
                      label: 'Start Date',
                      value: formatDateDDMMYYYY(item.event_start_date),
                    },
                  ]
                : []),
              ...(item.event_end_date
                ? [
                    {
                      label: 'End Date',
                      value: formatDateDDMMYYYY(item.event_end_date),
                    },
                  ]
                : []),
            ]
          : [
              ...(item.date
                ? [
                    {
                      label: 'Start Date',
                      value: formatDateDDMMYYYY(item.date),
                    },
                  ]
                : []),
              ...(item.end_date
                ? [
                    {
                      label: 'End Date',
                      value: formatDateDDMMYYYY(item.end_date),
                    },
                  ]
                : []),
            ]),
        ...(item.booking_start_date
          ? [
              {
                label: 'Booking Start Date',
                value: formatDateDDMMYYYY(item.booking_start_date),
              },
            ]
          : []),
        ...(item.booking_end_date
          ? [
              {
                label: 'Booking End Date',
                value: formatDateDDMMYYYY(item.booking_end_date),
              },
            ]
          : []),
        ...(item.district_id
          ? [{ label: 'District ID', value: item.district_id }]
          : []),
      ].flat()
    : []

  const stripHtml = (value?: string) =>
    value ? value.replace(/<[^>]*>/g, '').trim() : ''

  const getFieldText = (value: any) => {
    if (typeof value === 'string' || typeof value === 'number') {
      return String(value)
    }
    return ''
  }

  const handleDownloadDetailPDF = () => {
    const doc = new jsPDF()
    const title = `${isEvent ? 'Event' : 'Program'} Details`
    doc.setFontSize(16)
    doc.text(title, 14, 15)
    doc.setFontSize(10)
    doc.text(`Generated on: ${getCurrentDateDDMMYYYY()}`, 14, 22)

    const infoRows = itemFields.map((field) => [
      field.label,
      stripHtml(getFieldText(field.value)) || 'N/A',
    ])

    autoTable(doc, {
      head: [['Field', 'Value']],
      body: infoRows,
      startY: 30,
      styles: { fontSize: 10, cellPadding: 3 },
      headStyles: { fillColor: [59, 130, 246], textColor: 255 },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      margin: { left: 14, right: 14 },
    })

    if (booking.responses && booking.responses.length > 0) {
      const startY = (doc as any).lastAutoTable.finalY + 10
      const responseRows = booking.responses.map(
        (response: any, index: number) => [
          `Q${index + 1}: ${response.question?.question_text || 'N/A'}`,
          response.answer || 'No answer provided',
        ]
      )

      autoTable(doc, {
        head: [['Question', 'Answer']],
        body: responseRows,
        startY,
        styles: { fontSize: 10, cellPadding: 3 },
        headStyles: { fillColor: [34, 197, 94], textColor: 255 },
        alternateRowStyles: { fillColor: [245, 245, 245] },
        margin: { left: 14, right: 14 },
      })
    }

    doc.save(`${isEvent ? 'event' : 'program'}-details-${booking.id}.pdf`)
  }

  return (
    <div className="p-2">
      <div className="mb-5">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-bold text-blue-600 bg-blue-50 border border-blue-200 hover:bg-blue-100 hover:shadow-md transition-all duration-200"
        >
          ← Back to List
        </button>
      </div>

      {/* Program/Event Information */}
      <div className="bg-white rounded-2xl shadow-[0_2px_16px_rgba(0,0,0,0.06)] border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 bg-gradient-to-r from-blue-50/80 via-indigo-50/50 to-transparent border-b border-blue-100/60 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-md shadow-blue-200">
              <span className="text-white text-sm">📋</span>
            </div>
            <h3 className="text-lg font-bold text-gray-900">
              {isEvent ? 'Event Details' : 'Program Details'}
            </h3>
          </div>
          <button
            onClick={handleDownloadDetailPDF}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold text-white bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 shadow-md hover:shadow-lg hover:scale-[1.03] active:scale-[0.97] transition-all duration-300"
          >
            <DownloadIcon className="w-4 h-4" />
            Download PDF
          </button>
        </div>
        <div className="p-6">
          {itemFields.length > 0 ? (
            <div className="grid gap-3 sm:grid-cols-2">
              {itemFields.map((field, index) => (
                <div
                  key={index}
                  className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 p-3 rounded-xl bg-gray-50/80 border border-gray-100 hover:bg-blue-50/40 transition-colors duration-200"
                >
                  <div className="sm:w-2/5 text-xs font-bold text-gray-500 uppercase tracking-wider">
                    {field.label}
                  </div>
                  <div className="sm:w-3/5 text-sm font-semibold text-gray-900">
                    {field.value || 'N/A'}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              No details available.
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

const ProgramList: React.FC<{
  perPage?: number
  setPerPage?: (val: number) => void
}> = ({ perPage, setPerPage }) => {
  const [page, setPage] = useState(1)
  const [selectedProgramId, setSelectedProgramId] = useState<number | null>(
    null
  )
  const {
    data: response,
    isLoading,
    isError,
  } = useFetchProgramBookings({ page, page_size: perPage || 10 })
  const programs: ProgramItem[] = response?.data || []
  const totalPages = response?.meta?.last_page || 1
  const [activeRowId, setActiveRowId] = useState<number | null>(null)
  const [feedbackBooking, setFeedbackBooking] = useState<ProgramItem | null>(
    null
  )

  const handleDownloadPDF = () => {
    const doc = new jsPDF()

    // Add title
    doc.setFontSize(16)
    doc.text('Program Bookings Report', 14, 15)

    // Add generation date
    doc.setFontSize(10)
    doc.text(`Generated on: ${getCurrentDateDDMMYYYY()}`, 14, 22)

    // Prepare table data
    const tableData = programs.map((item) => [
      // item.id.toString(),
      capitalizeFirstLetter(item.program?.name || item.event?.name || 'N/A'),
      item.booking_status,
      item.created_at ? formatDateDDMMYYYY(item.created_at) : 'N/A',
      item.fees === '0.00' || item.fees === '0'
        ? 'Free'
        : `Rs.${parseFloat(item.fees).toLocaleString('en-IN')}`,
    ])

    // Add table
    autoTable(doc, {
      head: [['Name', 'Status', 'Applied Date', 'Fees']],
      body: tableData,
      startY: 30,
      styles: {
        fontSize: 10,
        cellPadding: 3,
      },
      headStyles: {
        fillColor: [59, 130, 246],
        textColor: 255,
        fontStyle: 'bold',
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245],
      },
      margin: { left: 14, right: 14 },
    })

    // Add footer
    const finalY = (doc as any).lastAutoTable.finalY || 30
    doc.setFontSize(10)
    doc.text(`Total Records: ${programs.length}`, 14, finalY + 10)
    doc.text(`Page ${page} of ${totalPages}`, 14, finalY + 15)

    // Save the PDF
    doc.save(`program-bookings-${new Date().toISOString().split('T')[0]}.pdf`)
  }

  if (isLoading)
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-4">
        <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
        <p className="text-gray-500 text-sm font-medium">
          Loading registered programs...
        </p>
      </div>
    )
  if (isError)
    return (
      <div className="text-center py-16">
        <div className="w-16 h-16 mx-auto mb-4 bg-red-50 rounded-full flex items-center justify-center">
          <span className="text-3xl">⚠️</span>
        </div>
        <p className="text-red-500 font-medium">Error loading data</p>
      </div>
    )
  if (programs.length === 0)
    return (
      <div className="text-center py-16">
        <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
          <span className="text-3xl">🎓</span>
        </div>
        <p className="text-gray-500 font-medium">No program bookings found</p>
      </div>
    )

  if (selectedProgramId) {
    return (
      <ProgramDetail
        id={String(selectedProgramId)}
        onBack={() => {
          setSelectedProgramId(null)
          setActiveRowId(null)
        }}
      />
    )
  }

  return (
    <div className="mt-4">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-50 border border-blue-100">
          <span className="w-2 h-2 rounded-full bg-blue-500" />
          <p className="text-sm text-blue-700 font-semibold">
            {programs.length} booking{programs.length !== 1 ? 's' : ''} on this
            page
          </p>
        </div>
        <button
          onClick={handleDownloadPDF}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold text-white bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 shadow-[0_4px_14px_rgba(59,130,246,0.35)] hover:shadow-[0_6px_18px_rgba(59,130,246,0.45)] hover:scale-[1.03] active:scale-[0.97] transition-all duration-300"
        >
          <DownloadIcon className="w-4 h-4" />
          Download PDF
        </button>
      </div>

      {/* Table card */}
      <div className="bg-white rounded-2xl shadow-[0_2px_16px_rgba(0,0,0,0.06)] border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-gradient-to-r from-blue-50 via-indigo-50/60 to-transparent border-b border-blue-100">
                {/* <th className="px-5 py-3.5 text-left text-xs font-bold text-blue-700 uppercase tracking-wider">
                  ID
                </th> */}
                <th className="px-5 py-3.5 text-left text-xs font-bold text-blue-700 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-5 py-3.5 text-left text-xs font-bold text-blue-700 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-5 py-3.5 text-left text-xs font-bold text-blue-700 uppercase tracking-wider">
                  Applied Date
                </th>
                <th className="px-5 py-3.5 text-left text-xs font-bold text-blue-700 uppercase tracking-wider">
                  Fees
                </th>
                <th className="px-5 py-3.5 text-left text-xs font-bold text-blue-700 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {programs.map((item) => (
                <tr
                  key={item.id}
                  className="border-b border-gray-50 hover:bg-blue-50/40 transition-colors duration-200"
                >
                  {/* <td className="px-5 py-3.5">
                    <span className="inline-flex items-center justify-center min-w-[2rem] h-7 px-2 rounded-lg bg-gray-100 text-gray-600 text-xs font-bold">
                      #{item.id}
                    </span>
                  </td> */}
                  <td className="px-5 py-3.5 font-semibold text-gray-900">
                    {capitalizeFirstLetter(
                      item.program?.name || item.event?.name || 'N/A'
                    )}
                  </td>
                  <td className="px-5 py-3.5">
                    <span
                      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold ${
                        item.booking_status === 'confirm'
                          ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                          : item.booking_status === 'reject'
                            ? 'bg-rose-50 text-rose-700 border border-rose-200'
                            : 'bg-amber-50 text-amber-700 border border-amber-200'
                      }`}
                    >
                      <span
                        className={`w-1.5 h-1.5 rounded-full ${
                          item.booking_status === 'confirm'
                            ? 'bg-emerald-500'
                            : item.booking_status === 'reject'
                              ? 'bg-rose-500'
                              : 'bg-amber-500'
                        }`}
                      />
                      {item.booking_status}
                    </span>
                  </td>
                  <td className="px-5 py-3.5 text-gray-600">
                    {item.created_at
                      ? formatDateDDMMYYYY(item.created_at)
                      : 'N/A'}
                  </td>
                  <td className="px-5 py-3.5">
                    <span className="font-bold text-gray-900">
                      {item.fees === '0.00' || item.fees === '0'
                        ? 'Free'
                        : `₹${parseFloat(item.fees).toLocaleString('en-IN')}`}
                    </span>
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-2">
                      {activeRowId === item.id ? (
                        <button
                          onClick={() => setSelectedProgramId(item.id)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-blue-600 bg-blue-50 border border-blue-200 hover:bg-blue-100 transition-colors duration-200"
                        >
                          <EyeIcon className="w-3.5 h-3.5" />
                          View
                        </button>
                      ) : (
                        <button
                          onClick={() => setActiveRowId(item.id)}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-gray-500 bg-gray-50 border border-gray-200 hover:bg-gray-100 hover:text-gray-700 transition-colors duration-200"
                          aria-label="Show actions"
                        >
                          <MoreIcon className="w-3.5 h-3.5" />
                        </button>
                      )}
                      {item.feedback_available && !item.feedback_submitted && (
                        <button
                          type="button"
                          onClick={() => setFeedbackBooking(item)}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold text-white bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 shadow-sm hover:shadow-md transition-all duration-200"
                        >
                          ★ Feedback
                        </button>
                      )}
                      {item.feedback_submitted && (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200">
                          ✓ Submitted
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {programs.length > 0 && (
        <div className="flex justify-between items-center gap-3 mt-6 px-4">
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-500">Per Page:</label>
            <select
              value={perPage || 10}
              onChange={(e) => {
                const size = Number(e.target.value)
                setPage(1)
                setPerPage?.(size)
              }}
              className="text-xs border border-gray-300 rounded px-2 py-1 bg-white"
            >
              {[5, 10, 20, 50, 100].map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
              disabled={page === 1}
              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-bold border transition-all duration-200 ${
                page === 1
                  ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                  : 'bg-white text-blue-600 border-blue-200 hover:bg-blue-50 hover:shadow-md'
              }`}
            >
              <Icons name="pagination-left-arrow" />
              Prev
            </button>
            <div className="flex items-center gap-1.5">
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(
                  (p) => p === 1 || p === totalPages || Math.abs(p - page) <= 1
                )
                .map((p, idx, arr) => (
                  <React.Fragment key={p}>
                    {idx > 0 && arr[idx - 1] !== p - 1 && (
                      <span className="text-gray-400 text-xs px-0.5">…</span>
                    )}
                    <button
                      onClick={() => setPage(p)}
                      className={`w-9 h-9 rounded-xl text-sm font-bold transition-all duration-200 ${
                        p === page
                          ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-lg shadow-blue-200'
                          : 'bg-white text-gray-600 border border-gray-200 hover:bg-blue-50 hover:text-blue-600'
                      }`}
                    >
                      {p}
                    </button>
                  </React.Fragment>
                ))}
            </div>
            <button
              onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
              disabled={page === totalPages}
              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-bold border transition-all duration-200 ${
                page === totalPages
                  ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                  : 'bg-white text-blue-600 border-blue-200 hover:bg-blue-50 hover:shadow-md'
              }`}
            >
              Next
              <Icons name="pagination-right-arrow" />
            </button>
          </div>
        </div>
      )}

      {feedbackBooking && (
        <ProgramFeedbackForm
          booking={feedbackBooking}
          onClose={() => setFeedbackBooking(null)}
          onSubmitted={() => setFeedbackBooking(null)}
        />
      )}
    </div>
  )
}

export default ProgramList
